import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, CheckCircle, Printer, Upload, X } from 'lucide-react';
import { CLINIC_ID } from './supabase.js';
import { loadInformedConsent, saveInformedConsent } from './data.js';
import { uploadImageFile } from './imageStorage.js';
import { dateLabel, friendlyError } from './lib/format.js';

async function compressImage(file, maxPx = 1920, quality = 0.85) {
  return new Promise((resolve) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const ratio = Math.min(maxPx / img.width, maxPx / img.height, 1);
      const canvas = document.createElement('canvas');
      canvas.width  = Math.round(img.width  * ratio);
      canvas.height = Math.round(img.height * ratio);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob(
        (blob) => resolve(blob ? new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }) : file),
        'image/jpeg', quality,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

export function InformedConsentModal({ visit, client, clinicName, onClose, onSaved }) {
  const [tab, setTab]                   = useState('print');
  const [consent, setConsent]           = useState(null);
  const [procedures, setProcedures]     = useState('');
  const [loadingConsent, setLoadingConsent] = useState(true);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState('');
  const [notice, setNotice]             = useState('');

  // Upload state
  const [file, setFile]                 = useState(null);
  const [preview, setPreview]           = useState(null);
  const [busy, setBusy]                 = useState(false);

  // Camera state
  const [cameraActive, setCameraActive] = useState(false);
  const [stream, setStream]             = useState(null);
  const videoRef   = useRef(null);
  const canvasRef  = useRef(null);
  const fileRef    = useRef(null);

  const stopStream = useCallback((s) => {
    if (s) s.getTracks().forEach((t) => t.stop());
  }, []);
  useEffect(() => () => stopStream(stream), [stream, stopStream]);

  // Load existing consent on mount
  useEffect(() => {
    (async () => {
      try {
        const data = await loadInformedConsent(visit.id);
        setConsent(data);
        if (data?.procedures_listed) setProcedures(data.procedures_listed);
      } catch (err) {
        setError(friendlyError(err));
      } finally {
        setLoadingConsent(false);
      }
    })();
  }, [visit.id]);

  async function saveProcedures() {
    if (!procedures.trim()) return;
    setSaving(true);
    setError('');
    try {
      await saveInformedConsent(visit.id, { procedures_listed: procedures.trim() });
      const updated = await loadInformedConsent(visit.id);
      setConsent(updated);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSaving(false);
    }
  }

  function handlePrint() {
    saveProcedures();
    const visitDateStr2 = visit.visit_date
      ? new Date(visit.visit_date).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })
      : '—';
    const html = `<!DOCTYPE html>
<html lang="id">
<head>
  <meta charset="UTF-8">
  <title>Informed Consent – ${visit.visit_number || ''}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Times New Roman', serif; padding: 32px 40px; font-size: 13px; color: #000; }
    .header { text-align: center; margin-bottom: 16px; }
    .header h2 { font-size: 17px; letter-spacing: 1px; margin-bottom: 2px; }
    .header p { font-size: 12px; }
    hr { border: none; border-top: 1px solid #000; margin: 12px 0; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 4px; }
    td { padding: 4px 0; vertical-align: top; }
    td:first-child { width: 170px; }
    .procedures-title { font-weight: bold; margin: 12px 0 6px; }
    .procedures-body { min-height: 70px; white-space: pre-wrap; line-height: 1.7; padding: 4px 0; }
    .statement { font-size: 12px; line-height: 1.7; margin: 16px 0; }
    .sig-row { display: flex; justify-content: space-between; margin-top: 40px; }
    .sig-box { width: 44%; text-align: center; }
    .sig-space { height: 56px; }
    .sig-line { border-top: 1px solid #000; padding-top: 6px; font-size: 12px; }
    @media print { @page { margin: 20mm; } }
  </style>
</head>
<body>
  <div class="header">
    <h2>INFORMED CONSENT</h2>
    <p>${clinicName || 'Klinik'}</p>
  </div>
  <hr>
  <table>
    <tr><td>Nama Pasien</td><td>: <strong>${client?.name || '—'}</strong></td></tr>
    <tr><td>No. Kunjungan</td><td>: ${visit.visit_number || '—'}</td></tr>
    <tr><td>Tanggal</td><td>: ${visitDateStr2}</td></tr>
    <tr><td>Tenaga Kesehatan</td><td>: ${visit.doctor_name || '—'}</td></tr>
  </table>
  <hr>
  <div class="procedures-title">Tindakan yang akan dilakukan:</div>
  <div class="procedures-body">${(procedures.trim() || '—').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>
  <hr>
  <div class="statement">
    Saya yang bertanda tangan di bawah ini menyatakan bahwa saya telah mendapatkan penjelasan
    yang cukup tentang tindakan medis yang akan dilakukan, termasuk manfaat, risiko, dan
    alternatif tindakan. Setelah memahami penjelasan tersebut, saya dengan sukarela
    memberikan persetujuan untuk dilakukan tindakan di atas.
  </div>
  <div class="sig-row">
    <div class="sig-box">
      <div class="sig-space"></div>
      <div class="sig-line">( ${(client?.name || '').replace(/</g, '&lt;')} )<br>Pasien / Wali</div>
    </div>
    <div class="sig-box">
      <div class="sig-space"></div>
      <div class="sig-line">( ${(visit.doctor_name || '').replace(/</g, '&lt;')} )<br>Tenaga Kesehatan</div>
    </div>
  </div>
  <script>window.onload = function(){ window.print(); };</script>
</body>
</html>`;
    const pw = window.open('', '_blank', 'width=700,height=900');
    if (!pw) return setError('Popup diblokir browser. Izinkan popup untuk mencetak.');
    pw.document.write(html);
    pw.document.close();
  }

  // Camera
  async function openCamera() {
    setError('');
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 } },
        audio: false,
      });
      setStream(mediaStream);
      setCameraActive(true);
      requestAnimationFrame(() => {
        if (videoRef.current) videoRef.current.srcObject = mediaStream;
      });
    } catch (err) {
      setError(`Kamera tidak dapat dibuka: ${err.message}`);
    }
  }

  function closeCamera() {
    stopStream(stream);
    setStream(null);
    setCameraActive(false);
  }

  function capturePhoto() {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return setError('Gagal mengambil foto dari kamera.');
      const f = new File([blob], `consent_${Date.now()}.jpg`, { type: 'image/jpeg' });
      setFile(f);
      setPreview(URL.createObjectURL(f));
      closeCamera();
    }, 'image/jpeg', 0.92);
  }

  function pickFile(e) {
    const f = e.target.files?.[0];
    if (!f || !f.type.startsWith('image/')) return setError('Hanya file gambar (JPG, PNG, WEBP).');
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setError('');
  }

  async function uploadConsent() {
    if (!file) return setError('Pilih atau ambil foto consent terlebih dahulu.');
    setError('');
    setBusy(true);
    try {
      const compressed = await compressImage(file);
      const path = `${CLINIC_ID}/${visit.id}/consent_${Date.now()}.jpg`;
      const publicUrl = await uploadImageFile(compressed, path, { upsert: true });
      await saveInformedConsent(visit.id, {
        storage_url: publicUrl,
        signed_at: new Date().toISOString(),
        procedures_listed: procedures.trim() || consent?.procedures_listed || null,
      });
      setConsent(await loadInformedConsent(visit.id));
      setFile(null);
      setPreview(null);
      setNotice('Foto consent berhasil diunggah. Status: SIGNED ✓');
      if (onSaved) onSaved();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  const isSigned = !!consent?.storage_url;
  const visitDateStr = visit.visit_date
    ? new Date(visit.visit_date).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })
    : '—'; // dipakai di preview modal

  return (
    <>
      {/* Modal UI */}
      <div className="modal-backdrop" role="presentation"
        onMouseDown={(e) => e.target === e.currentTarget && !cameraActive && onClose()}>
        <section className="modal-card" role="dialog" aria-modal="true" aria-label="Informed Consent">

          <header className="modal-head">
            <div className="modal-title-icon">
              {isSigned
                ? <CheckCircle size={23} color="#2f6f4f" />
                : <Printer size={23} />}
            </div>
            <div className="grow">
              <h2>Informed Consent</h2>
              <p>
                {visit.visit_number} · {client?.name || '—'}
                {isSigned && <span style={{ color: '#2f6f4f', fontWeight: 700, marginLeft: 10 }}>✓ SIGNED</span>}
              </p>
            </div>
            <button className="icon-button" onClick={cameraActive ? closeCamera : onClose} aria-label="Tutup">
              <X size={20} />
            </button>
          </header>

          {cameraActive ? (
            <div className="camera-view">
              <video ref={videoRef} autoPlay playsInline muted className="camera-video" />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
              <div className="camera-controls">
                <button type="button" className="button button-quiet" onClick={closeCamera}>Batal</button>
                <button type="button" className="camera-shutter" onClick={capturePhoto} aria-label="Ambil foto">
                  <span className="camera-shutter-inner" />
                </button>
                <div style={{ width: 80 }} />
              </div>
            </div>
          ) : (
            <>
              <div className="photo-tab-bar">
                <button className={`photo-tab ${tab === 'print' ? 'photo-tab-active' : ''}`}
                  onClick={() => setTab('print')}>
                  <Printer size={15} /> Cetak Form
                </button>
                <button className={`photo-tab ${tab === 'upload' ? 'photo-tab-active' : ''}`}
                  onClick={() => setTab('upload')}>
                  <Upload size={15} /> Upload Foto
                  {isSigned && <CheckCircle size={13} color="#2f6f4f" style={{ marginLeft: 4 }} />}
                </button>
              </div>

              <div className="modal-body photo-body">
                {error  ? <div className="alert alert-danger">{error}</div> : null}
                {notice ? (
                  <div className="alert alert-success">
                    {notice}
                    <button className="alert-close" onClick={() => setNotice('')}>×</button>
                  </div>
                ) : null}

                {loadingConsent ? (
                  <div className="content-loading"><div className="loader" /><span>Memuat data consent…</span></div>
                ) : tab === 'print' ? (
                  <>
                    <div className="form-field form-field-wide">
                      <span style={{ fontWeight: 600, fontSize: 13 }}>Daftar Tindakan</span>
                      <p style={{ fontSize: 12, color: '#888', margin: '2px 0 6px' }}>
                        Ketik tindakan yang akan dilakukan. Gunakan enter untuk pisah baris.
                      </p>
                      <textarea
                        rows={5}
                        value={procedures}
                        onChange={(e) => setProcedures(e.target.value)}
                        placeholder={'Contoh:\n- Akupunktur 30 titik\n- Bekam basah 4 titik\n- Totok wajah'}
                        style={{ resize: 'vertical', width: '100%', padding: '10px 12px',
                          border: '1px solid #e8e2d5', borderRadius: 8, fontSize: 14, fontFamily: 'inherit' }}
                      />
                    </div>

                    {/* Mini preview */}
                    <div className="consent-preview-box">
                      <div className="consent-preview-label">Preview form cetak</div>
                      <div className="consent-preview">
                        <div className="cp-header">
                          <strong>INFORMED CONSENT</strong>
                          <span>{clinicName || 'Klinik'}</span>
                        </div>
                        <div className="cp-row"><span>Pasien</span><span>{client?.name || '—'}</span></div>
                        <div className="cp-row"><span>Kunjungan</span><span>{visit.visit_number || '—'}</span></div>
                        <div className="cp-row"><span>Tanggal</span><span>{visitDateStr}</span></div>
                        <div className="cp-row"><span>Nakes</span><span>{visit.doctor_name || '—'}</span></div>
                        <div className="cp-divider" />
                        <div className="cp-procedures">
                          <strong>Tindakan:</strong>
                          <p>{procedures.trim() || <em style={{ color: '#aaa' }}>belum diisi</em>}</p>
                        </div>
                        <div className="cp-sig-row">
                          <div className="cp-sig"><div className="cp-sig-line" />Pasien / Wali</div>
                          <div className="cp-sig"><div className="cp-sig-line" />Tenaga Kesehatan</div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  /* Upload tab */
                  <>
                    {isSigned && (
                      <div style={{ marginBottom: 12 }}>
                        <p style={{ fontSize: 13, color: '#2f6f4f', fontWeight: 600, margin: '0 0 8px' }}>
                          ✓ Consent sudah ditandatangani — {dateLabel(consent.signed_at, { time: true })}
                        </p>
                        <a href={consent.storage_url} target="_blank" rel="noopener noreferrer"
                          style={{ fontSize: 13 }}>
                          Lihat foto consent →
                        </a>
                      </div>
                    )}

                    <input ref={fileRef} type="file" accept="image/*"
                      style={{ display: 'none' }} onChange={pickFile} />

                    <div className="photo-pick-row">
                      <button type="button" className="button button-soft photo-pick-btn"
                        onClick={openCamera} disabled={busy}>
                        <Camera size={18} /> Ambil Foto
                      </button>
                      <button type="button" className="button button-quiet photo-pick-btn"
                        onClick={() => fileRef.current?.click()} disabled={busy}>
                        <Upload size={18} /> Pilih dari Galeri
                      </button>
                    </div>

                    {preview && (
                      <div className="photo-preview-grid">
                        <div className="photo-preview-item">
                          <img src={preview} alt="Preview consent" />
                          <button className="photo-preview-remove"
                            onClick={() => { setFile(null); setPreview(null); }}
                            aria-label="Hapus"><X size={13} /></button>
                        </div>
                      </div>
                    )}

                    <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
                      Foto form consent yang sudah ditandatangani pasien dan nakes.
                      {isSigned ? ' Upload ulang untuk mengganti.' : ''}
                    </p>
                  </>
                )}
              </div>

              <footer className="modal-actions">
                <button className="button button-quiet" onClick={onClose} disabled={busy || saving}>
                  Tutup
                </button>
                {tab === 'print' ? (
                  <button className="button button-primary" onClick={handlePrint} disabled={saving}>
                    <Printer size={17} />
                    {saving ? 'Menyimpan…' : 'Simpan & Cetak'}
                  </button>
                ) : (
                  <button className="button button-primary" onClick={uploadConsent}
                    disabled={busy || !file}>
                    <Upload size={17} />
                    {busy ? 'Mengunggah…' : isSigned ? 'Upload Ulang' : 'Upload Foto Signed'}
                  </button>
                )}
              </footer>
            </>
          )}
        </section>
      </div>

      <style>{`
        .consent-preview-box {
          border: 1px solid #e8e2d5;
          border-radius: 10px;
          overflow: hidden;
          margin-top: 12px;
        }
        .consent-preview-label {
          background: #f7f3ec;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: .05em;
          color: #9a8e7a;
          padding: 6px 14px;
          border-bottom: 1px solid #e8e2d5;
        }
        .consent-preview {
          padding: 14px 16px;
          font-size: 13px;
          background: #fff;
        }
        .cp-header {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-bottom: 10px;
          gap: 2px;
        }
        .cp-header strong { font-size: 14px; letter-spacing: .06em; }
        .cp-header span   { font-size: 11px; color: #888; }
        .cp-row {
          display: flex;
          justify-content: space-between;
          padding: 3px 0;
          border-bottom: 1px dotted #e8e2d5;
          font-size: 12px;
        }
        .cp-row span:first-child { color: #888; }
        .cp-divider { border-top: 1px solid #d6cfc0; margin: 8px 0; }
        .cp-procedures { font-size: 12px; margin-bottom: 12px; }
        .cp-procedures strong { display: block; margin-bottom: 4px; }
        .cp-procedures p { white-space: pre-wrap; margin: 0; }
        .cp-sig-row {
          display: flex;
          justify-content: space-between;
          margin-top: 10px;
        }
        .cp-sig {
          width: 45%;
          font-size: 11px;
          color: #888;
          text-align: center;
        }
        .cp-sig-line {
          height: 24px;
          border-bottom: 1px solid #888;
          margin-bottom: 4px;
        }
      `}</style>
    </>
  );
}
